import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminAPI, leadsAPI, contactsAPI } from '@utils/api'
import AdminLayout from '@components/admin/AdminLayout'
import {
  Users, BookOpen, CalendarDays, TrendingUp, ArrowUpRight,
  Phone, Mail, Loader2, Star, Inbox, Plus, MessageSquare,
  ChevronRight, BarChart2, Activity
} from 'lucide-react'
import { format } from 'date-fns'

const STATUS_CLS = {
  New:            'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  Contacted:      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  Interested:     'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  Enrolled:       'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  Converted:      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  Lost:           'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'Not Interested':'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
}
const PRIORITY_CLS = {
  Urgent: 'text-red-500', High: 'text-orange-500', Medium: 'text-yellow-500', Low: 'text-gray-400',
}

const Card = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm ${className}`}>
    {children}
  </div>
)

const AdminDashboard = () => {
  const [stats,   setStats]   = useState(null)
  const [leads,   setLeads]   = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      const [sR, lR] = await Promise.allSettled([
        adminAPI.getDashboardStats(),
        leadsAPI.getAll({ limit: 6, sortBy: 'createdAt', sortOrder: 'desc' }),
      ])
      if (sR.status === 'fulfilled') setStats(sR.value.data)
      if (lR.status === 'fulfilled') setLeads(lR.value.data || [])
      setLoading(false)
    }
    load()
  }, [])

  const statCards = stats ? [
    {
      label: 'Total Leads', value: stats.counts.totalLeads,
      sub: `${stats.counts.newLeads} new`, icon: Users,
      gradient: 'from-blue-500 to-cyan-500', bg: 'bg-blue-50 dark:bg-blue-900/20',
      link: '/admin/leads',
    },
    {
      label: 'Active Courses', value: stats.counts.totalCourses,
      sub: 'Published', icon: BookOpen,
      gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-50 dark:bg-violet-900/20',
      link: '/admin/courses',
    },
    {
      label: 'Batches', value: stats.counts.totalBatches,
      sub: `${stats.counts.upcomingBatches} upcoming`, icon: CalendarDays,
      gradient: 'from-orange-500 to-red-500', bg: 'bg-orange-50 dark:bg-orange-900/20',
      link: '/admin/batches',
    },
    {
      label: 'Conversion', value: `${stats.conversionRate?.toFixed(1)}%`,
      sub: `${stats.counts.convertedLeads} converted`, icon: TrendingUp,
      gradient: 'from-green-500 to-emerald-500', bg: 'bg-green-50 dark:bg-green-900/20',
      link: '/admin/leads',
    },
    {
      label: 'Contacts', value: stats.counts.totalContacts ?? 0,
      sub: `${stats.counts.newContacts ?? 0} new`, icon: MessageSquare,
      gradient: 'from-pink-500 to-rose-500', bg: 'bg-pink-50 dark:bg-pink-900/20',
      link: '/admin/contacts',
    },
  ] : []

  const quickLinks = [
    { label: 'Add Course',  to: '/admin/courses',  icon: BookOpen,     color: 'text-violet-600 bg-violet-50 dark:bg-violet-900/20' },
    { label: 'Add Batch',   to: '/admin/batches',   icon: CalendarDays, color: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20' },
    { label: 'View Leads',  to: '/admin/leads',     icon: Users,        color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20' },
    { label: 'Contacts',    to: '/admin/contacts',  icon: Inbox,        color: 'text-pink-600 bg-pink-50 dark:bg-pink-900/20' },
  ]

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    </AdminLayout>
  )

  return (
    <AdminLayout>
      <div className="space-y-5">

        {/* Page title */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white">Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">Welcome back! Here's what's happening.</p>
          </div>
          <span className="text-xs text-gray-400 hidden sm:block">{format(new Date(), 'EEEE, dd MMM yyyy')}</span>
        </div>

        {/* Stat cards — 2 col mobile, 4 col desktop */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
          {statCards.map((card, i) => {
            const Icon = card.icon
            return (
              <Link key={i} to={card.link}>
                <Card className="p-4 sm:p-5 hover:shadow-md transition-shadow cursor-pointer group">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center shrink-0`}>
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <ArrowUpRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-primary-500 transition-colors" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white leading-none">{card.value}</div>
                  <div className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1">{card.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{card.sub}</div>
                </Card>
              </Link>
            )
          })}
        </div>

        {/* Main 2-col grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-5">

          {/* Recent Leads — 2/3 width */}
          <Card className="xl:col-span-2 overflow-hidden">
            <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary-600" />
                <h2 className="font-bold text-sm text-gray-900 dark:text-white">Recent Leads</h2>
              </div>
              <Link to="/admin/leads" className="flex items-center gap-1 text-xs text-primary-600 hover:underline font-medium">
                View all <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="divide-y divide-gray-50 dark:divide-gray-800/60">
              {leads.length === 0 && (
                <div className="px-5 py-10 text-center text-gray-400 text-sm">No leads yet</div>
              )}
              {leads.map((lead) => (
                <div key={lead._id} className="flex items-center gap-3 px-4 sm:px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/30 dark:to-accent-900/30 flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-primary-700 dark:text-primary-400">{lead.name?.[0]?.toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-gray-900 dark:text-white truncate">{lead.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${STATUS_CLS[lead.status] || ''}`}>{lead.status}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                      <span className="hidden sm:flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>
                      <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0" /><span className="truncate">{lead.email}</span></span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-xs font-semibold ${PRIORITY_CLS[lead.priority] || ''}`}>{lead.priority}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{lead.createdAt ? format(new Date(lead.createdAt), 'MMM d') : '—'}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Right column — 1/3 width */}
          <div className="flex flex-col gap-4">

            {/* Quick links */}
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Plus className="w-4 h-4 text-primary-600" />
                <h2 className="font-bold text-sm text-gray-900 dark:text-white">Quick Actions</h2>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quickLinks.map(q => {
                  const Icon = q.icon
                  return (
                    <Link key={q.to} to={q.to}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl text-xs font-semibold ${q.color} hover:opacity-80 transition-opacity`}>
                      <Icon className="w-5 h-5" />
                      {q.label}
                    </Link>
                  )
                })}
              </div>
            </Card>

            {/* Lead Status breakdown */}
            <Card className="p-4 flex-1">
              <div className="flex items-center gap-2 mb-3">
                <BarChart2 className="w-4 h-4 text-primary-600" />
                <h2 className="font-bold text-sm text-gray-900 dark:text-white">Lead Status</h2>
              </div>
              <div className="space-y-2">
                {(stats?.leadStatusBreakdown || []).map((item) => {
                  const total = stats?.counts?.totalLeads || 1
                  const pct = Math.round((item.count / total) * 100)
                  return (
                    <div key={item._id}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-600 dark:text-gray-400 font-medium">{item._id}</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_CLS[item._id] || 'bg-gray-100 text-gray-600'}`}>{item.count}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  )
                })}
                {(stats?.leadStatusBreakdown || []).length === 0 && (
                  <p className="text-xs text-gray-400">No data yet</p>
                )}
              </div>
            </Card>

          </div>
        </div>

        {/* Bottom row — Top Courses */}
        {(stats?.popularCourses || []).length > 0 && (
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-primary-600" />
                <h2 className="font-bold text-sm text-gray-900 dark:text-white">Top Courses</h2>
              </div>
              <Link to="/admin/courses" className="flex items-center gap-1 text-xs text-primary-600 hover:underline font-medium">
                Manage <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-100 dark:bg-gray-800">
              {(stats?.popularCourses || []).map((c) => (
                <div key={c._id} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
                    <BookOpen className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{c.title}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      {c.enrolledStudents} enrolled &middot;
                      <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                      {c.rating?.average?.toFixed(1) || '—'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

      </div>
    </AdminLayout>
  )
}

export default AdminDashboard
